import { sharedFeedbackValue, isSharedRemoteFeedback, sharedFeedbackText, type SharedRemoteFeedback } from "./sharedFeedbackPresentation";
import { getI18n, useTranslation } from "react-i18next";
import { createElement, Fragment, type ReactNode } from "react";
import source from "./locales/en/emailManagement";
import type { PromptOptions } from "../components/ui/Dialog";

export type EmailKey = { [K in keyof typeof source]: K extends `${infer Base}_one` | `${infer Base}_other` ? Base : K }[keyof typeof source];
export type EmailMessage = SharedRemoteFeedback | { key: EmailKey; values?: Record<string, string | number> };
export function emailText(key: EmailKey, values?: Record<string, string | number>): string {
  const engine = getI18n();
  // Flat producer keys contain dots; keep a narrow typed boundary instead of
  // recursively expanding every catalog key through i18next's selector type.
  if (engine?.isInitialized) return (engine as unknown as { t: (key: EmailKey, options: Record<string, unknown>) => string }).t(key, { replace: values, ...(typeof values?.count === "number" ? { count: values.count } : {}), ns: "emailManagement", keySeparator: false });
  const messages: Record<string, string> = source;
  const template = messages[key] ?? messages[`${key}_${values?.count === 1 ? "one" : "other"}`] ?? key;
  return template.replace(/{{\s*(\w+)\s*}}/g, (_match, name: string) => String(values?.[name] ?? ""));
}
export function useEmailLanguage(): string {
  const { i18n } = useTranslation();
  return i18n.resolvedLanguage ?? i18n.language ?? "en";
}
export const emailLocal = (key: EmailKey, values?: Record<string, string | number>): EmailMessage => ({ key, values });
export const emailMessage = (value: string | EmailMessage | null): string | null => typeof value === "object" && value !== null ? isSharedRemoteFeedback(value) ? sharedFeedbackText(value) : emailText(value.key, value.values) : value;
export const emailLocale = (): string => getI18n()?.resolvedLanguage ?? getI18n()?.language ?? "en";
export const emailNumber = (value: number): string => new Intl.NumberFormat(emailLocale()).format(value);
export const emailDialog = (renderText: () => PromptOptions): PromptOptions => ({ ...renderText(), renderText });
/** Insert React content into a complete plain-text message without catalog markup. */
export function emailSlot(key: EmailKey, name: string, node: ReactNode, values?: Record<string, string | number>): ReactNode {
  return emailText(key, { ...values, [name]: "\uFFFC" }).split("\uFFFC").map((part, index) => createElement(Fragment, { key: index }, index > 0 ? node : null, part));
}

const remoteMessages: Partial<Record<EmailKey, readonly string[]>> = {
  "email.forbidden": [],
  "email.settings.fetchFailed": [],
  "email.settings.updateFailed": [],
  "email.test.adminEmailMissing": [],
  "email.test.sendFailed": [],
  "email.advanced.fetchFailed": [],
  "email.advanced.updateFailed": [],
  "email.test.sent": [
    "recipient"
  ],
  "email.templates.listFailed": [],
  "email.templates.notFound": [],
  "email.templates.fetchFailed": [],
  "email.templates.updateFailed": [],
  "email.templates.notSystem": [],
  "email.templates.reset": [],
  "email.templates.resetFailed": [],
  "email.templates.previewFailed": [],
  "email.log.invalidQuery": [],
  "email.log.listFailed": [],
  "email.log.statsFailed": [],
  "email.log.invalidId": [],
  "email.log.deleteFailed": [],
  "email.log.invalidBody": [],
  "email.log.bulkDeleteFailed": [],
  "email.log.confirmRequired": [],
  "email.log.clearFailed": [],
  "email.dispatch.providerMissing": [],
  "email.dispatch.fromAddressMissing": [],
  "email.dispatch.smtpHostMissing": [],
  "email.dispatch.mailgunRequired": [],
  "email.dispatch.sendgridRequired": [],
  "email.dispatch.postmarkRequired": [],
  "email.dispatch.sparkpostRequired": [],
  "email.dispatch.sesRequired": [],
  "email.dispatch.unknownProvider": [
    "provider"
  ],
  "email.dispatch.unknownError": [],
  "email.dispatch.mailgunError": [
    "status",
    "message"
  ],
  "email.dispatch.sendgridError": [
    "status",
    "message"
  ],
  "email.dispatch.postmarkError": [
    "status",
    "message"
  ],
  "email.dispatch.sparkpostError": [
    "status",
    "message"
  ],
  "email.dispatch.sesError": [
    "status",
    "message"
  ],
  "email.template.security-new-login.name": [],
  "email.template.security-new-login.description": [],
  "email.template.security-password-changed.name": [],
  "email.template.security-password-changed.description": [],
  "email.template.system-disk-usage.name": [],
  "email.template.system-disk-usage.description": [],
  "email.template.app-offline.name": [],
  "email.template.app-offline.description": [],
  "email.template.account-welcome.name": [],
  "email.template.account-welcome.description": [],
  "email.template.account-activation.name": [],
  "email.template.account-activation.description": [],
  "email.template.system-monitor-alert.name": [],
  "email.template.system-monitor-alert.description": [],
  "email.template.vpn-routing-failback.name": [],
  "email.template.vpn-routing-failback.description": [],
  "email.template.vpn-routing-kill-switch.name": [],
  "email.template.vpn-routing-kill-switch.description": [],
  "email.template.password-reset.name": [],
  "email.template.password-reset.description": [],
  "email.template.test-email.name": [],
  "email.template.test-email.description": [],
  "email.template.vpn-routing-recovery-success.name": [],
  "email.template.vpn-routing-recovery-success.description": [],
  "email.template.vpn-routing-recovery-exhausted.name": [],
  "email.template.vpn-routing-recovery-exhausted.description": [],
  "email.template.vpn-config-staleness-warning.name": [],
  "email.template.vpn-config-staleness-warning.description": [],
  "email.template.vpn-config-dead.name": [],
  "email.template.vpn-config-dead.description": [],
  "email.template.vpn-config-stale.name": [],
  "email.template.vpn-config-stale.description": [],
  "email.template.security-mfa-changed.name": [],
  "email.template.security-mfa-changed.description": [],
  "email.template.app-online.name": [],
  "email.template.app-online.description": [],
  "email.template.app-update-available.name": [],
  "email.template.app-update-available.description": [],
  "email.template.app-updates-available.name": [],
  "email.template.app-updates-available.description": [],
  "email.template.qb-update-available.name": [],
  "email.template.qb-update-available.description": [],
  "email.template.dashboard-update-available.name": [],
  "email.template.dashboard-update-available.description": [],
  "email.template.signal-update-available.name": [],
  "email.template.signal-update-available.description": [],
  "email.template.news-announcement.name": [],
  "email.template.news-announcement.description": [],
  "email.template.news-announcement-updated.name": [],
  "email.template.news-announcement-updated.description": [],
  "email.template.app-updated-success.name": [],
  "email.template.app-updated-success.description": [],
  "email.template.app-update-failed.name": [],
  "email.template.app-update-failed.description": [],
  "email.template.job-completed.name": [],
  "email.template.job-completed.description": [],
  "email.template.job-failed.name": [],
  "email.template.job-failed.description": [],
  "email.template.media-share-link.name": [],
  "email.template.media-share-link.description": [],
  "email.template.security-attack-spike.name": [],
  "email.template.security-attack-spike.description": [],
  "email.validation.settings.smtp.port.range": [],
  "email.validation.settings.fromName.max": [],
  "email.validation.settings.smtp.host.max": [],
  "email.validation.settings.smtp.username.max": [],
  "email.validation.settings.smtp.password.max": [],
  "email.validation.settings.mailgun.apiKey.max": [],
  "email.validation.settings.mailgun.domain.max": [],
  "email.validation.settings.sendgrid.apiKey.max": [],
  "email.validation.settings.postmark.serverToken.max": [],
  "email.validation.settings.sparkpost.apiKey.max": [],
  "email.validation.settings.ses.accessKeyId.max": [],
  "email.validation.settings.ses.secretAccessKey.max": [],
  "email.validation.settings.ses.region.max": [],
  "email.validation.template.subject.min": [],
  "email.validation.template.subject.max": [],
  "email.validation.template.bodyHtml.max": [],
  "email.validation.template.bodyText.max": [],
  "email.validation.advanced.headerText.max": [],
  "email.validation.advanced.footerText.max": [],
  "email.validation.settings.fromAddress.email": [],
  "email.validation.settings.smtp.port.regex": [],
  "email.validation.required": [],
  "email.validation.invalidType": [
    "expected"
  ],
  "email.validation.invalidChoice": [
    "options"
  ],
  "email.validation.invalidInput": [],
  "email.validation.logBulk.min": [],
  "email.validation.logBulk.max": [],
  "email.validation.logBulk.positive": [],
  "email.validation.logBulk.integer": []
};
export function emailResponse(presentation: unknown, raw: unknown, fallback: EmailKey): string | EmailMessage {
  const shared = sharedFeedbackValue(raw, presentation);
  if (shared) return shared;
  if (presentation && typeof presentation === "object" && "code" in presentation && typeof presentation.code === "string" && Object.prototype.hasOwnProperty.call(remoteMessages, presentation.code)) {
    const code = presentation.code as EmailKey;
    const params = "params" in presentation && presentation.params && typeof presentation.params === "object" ? presentation.params as Record<string, unknown> : {};
    const values: Record<string, string | number> = {};
    let valid = true;
    for (const name of remoteMessages[code] ?? []) {
      const value = Object.prototype.hasOwnProperty.call(params, name) ? params[name] : undefined;
      if ((typeof value !== "string" && typeof value !== "number") || (typeof value === "number" && !Number.isFinite(value))) { valid = false; break; }
      values[name] = value;
    }
    if (valid) return emailLocal(code, values);
  }
  return typeof raw === "string" ? raw : emailLocal(fallback);
}
export function emailFieldText<T extends Partial<Record<string, string[]>>>(fields: T, presentations: Record<string, unknown>): T {
  return Object.fromEntries(Object.entries(fields).map(([field, messages]) => [field, messages?.map((message, index) => emailMessage(emailResponse(Array.isArray(presentations[field]) ? presentations[field][index] : undefined, message, "pleaseCheckTheFormForErrors"))!)])) as T;
}

/** Default metadata is translated only with explicit producer provenance. */
export function emailTemplateText(template: { name: string; description: string; presentation?: { name?: unknown; description?: unknown } }, field: "name" | "description"): string {
  return emailMessage(emailResponse(template.presentation?.[field], template[field], "failedToFetchTemplate"))!;
}
